<template>
  <div>
    <v-alert class="mb-4" type="warning" variant="tonal" density="compact">
      Дані пакета буде повністю замінено даними з файлу. Статус зміниться на
      «Новий», для підписання та відправлення пакет потрібно буде провалідувати
      повторно. Незбережені зміни буде втрачено.
    </v-alert>

    <package-source-select
      v-model="source"
      class="mb-4"
      :version="dialog.dialogData.version"
      :disabled="$loading.isLoading(['packageParse', 'packageReplace'])"
      @update:model-value="resetFile"></package-source-select>

    <div v-if="$loading.isLoading('packageParse')" class="mb-7">
      <div class="d-flex justify-center mb-5">
        <v-progress-circular
          indeterminate
          size="60"
          color="deep-purple-lighten-1"></v-progress-circular>
      </div>
      <div class="text-center">
        <div class="font-weight-bold mb-1">Триває розпізнавання файлу...</div>
        <div
          v-if="file?.length"
          class="d-flex align-center ga-2 justify-center mb-1">
          <v-icon icon="mdi-file-outline" color="grey" size="small"></v-icon>
          <span class="text-disabled font-weight-bold font-size-13">
            {{ file[0].split('/').at(-1) }}
          </span>
        </div>
        <div class="font-size-13 text-disabled">
          Зачекайте, це може зайняти декілька секунд
        </div>
      </div>
    </div>
    <div v-show="!$loading.isLoading('packageParse')">
      <b-input-file
        v-model="file"
        name-folder="packages"
        title="Файл пакету"
        dropper
        :multiple="false"
        :accept="fileAccept[source]"
        type="paths"
        label="Оберіть файл пакету"
        @update:model-value="parsePackage">
        <template #dropper-label>
          <b>
            Перетягніть сюди файл або
            <u>
              оберіть його <br />
              на своєму носієві
            </u>
          </b>
        </template>
      </b-input-file>
      <div class="mt-5 text-center">
        <div v-if="errorMessage" class="text-error">{{ errorMessage }}</div>
        <div v-else-if="parseResult" class="text-success">
          Дані успішно зчитані
        </div>
        <div v-else-if="v$.$error" class="text-error">Оберіть файл пакету</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, type PropType } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { BInputFile, type FileAccept } from 'best-modules/components'
import { setSnackbar, type Dialog } from 'best-modules/plugins'
import { download, getFile } from 'best-modules/utils'
import {
  parsePackage as parsePackageRequest,
  replacePackage,
} from '@/request/report'
import type {
  PackageParseResult,
  PackageParseType,
  PackageReplaceCredentials,
} from '@/utils/types'
import PackageSourceSelect from './PackageSourceSelect.vue'

defineOptions({
  name: 'PackageReplace',
})

const props = defineProps({
  dialog: {
    type: Object as PropType<Dialog<'PackageReplace'>>,
    required: true,
  },
})

const fileAccept: Record<PackageParseType, FileAccept[]> = {
  excel: ['.xls', '.xlsx'],
  json: ['.json'],
}

const source = ref<PackageParseType>('excel')
const file = ref<string[] | null>(null)
const parseResult = ref<PackageParseResult | null>(null)
const errorMessage = ref<string | null>(null)

const v$ = useVuelidate({ parseResult: { required } }, { parseResult })

const getRequestErrorMessage = (e: any, defaultMessage: string): string => {
  return e?.response?.data?.message || defaultMessage
}

const resetParseResult = () => {
  parseResult.value = null
  errorMessage.value = null
  v$.value.$reset()
}

const resetFile = () => {
  file.value = null
  resetParseResult()
}

const getParseResultError = (res: PackageParseResult): string | null => {
  if (res.parsedData?.length !== 1) {
    return 'Для заміни файл повинен містити рівно один пакет'
  }
  if (
    source.value === 'excel' &&
    res.parsedData[0].version !== props.dialog.dialogData.version
  ) {
    return 'Версія шаблону не відповідає версії пакета'
  }

  return null
}

const parsePackage = (paths: string[] | null) => {
  resetParseResult()
  const path = paths?.at(-1)
  if (!path) return

  file.value = [path]

  return parsePackageRequest({ file: path.substring(9) }, source.value)
    .then(res => {
      errorMessage.value = getParseResultError(res)
      parseResult.value = errorMessage.value ? null : res
    })
    .catch(e => {
      errorMessage.value = getRequestErrorMessage(e, 'Помилка зчитування даних')
    })
}

const downloadIdsFile = (path: string) => {
  return getFile(path)
    .then(localPath => download(localPath, path.split('/').at(-1)))
    .catch(() => {
      setSnackbar({
        text: 'Не вдалося завантажити файл зі згенерованими ID',
        color: 'error',
      })
    })
}

const getReplaceCredentials = (): PackageReplaceCredentials => {
  const [{ version, data }] = parseResult.value.parsedData

  return source.value === 'excel'
    ? { source: 'excel', version, data }
    : { source: 'json', data }
}

const submit = () => {
  const idsFile = source.value === 'excel' ? parseResult.value.file : null

  return replacePackage(
    props.dialog.dialogData.packageId,
    getReplaceCredentials()
  )
    .then(res => {
      if (idsFile) {
        downloadIdsFile(idsFile)
      }

      return res
    })
    .catch(e => {
      errorMessage.value = getRequestErrorMessage(
        e,
        'Не вдалося замінити пакет'
      )

      return Promise.reject(e)
    })
}

defineExpose({ submit, v$ })
</script>
